/* eslint-disable @typescript-eslint/no-unused-vars */
import { globalVar, isBrowser } from './runtime';
import { fromJSON, toJSON } from '../platform/json';

const STORE_PREFIX = 'SL';
const STORE_STAMP_SUFFIX = 'Expires';

const getItemKey = (key: string) => `${STORE_PREFIX}.${key}`;
const getStampKey = (key: string) => `${getItemKey(key)}.${STORE_STAMP_SUFFIX}`;

interface CacheStorage {
  has(key: string): boolean;
  clear(): void;
  clearBy(regex: RegExp): void;
  getItem(key: string): any;
  removeItem(key: string): any;
  setItem(key: string, data: any, stamp?: string): void;
  hasValid(key: string): boolean;
}

/**
 * Abstraction over localStorage
 *  
 * @class ClientCache
 */
export class ClientCache implements CacheStorage {

  public store: Storage = globalVar('localStorage');

  public has(key: string): boolean { return !!this.store.getItem(getItemKey(key)) }

  public setItem(key: string, data: any, stamp?: string): void {

    try {
      this.store.setItem(getItemKey(key), toJSON(data));

      if (!!stamp) {
        this.store.setItem(getStampKey(key), toJSON(stamp));
      }
    } catch (e) {
      throw new Error('Could not persist. localStorage might be full.\n\n' + e);
    }
  }

  public hasValid(key: string): boolean {

    const stamp = this.store.getItem(getStampKey(key));

    const expiresDate = !!stamp && new Date(fromJSON(stamp));
    
    return !!expiresDate && expiresDate.getTime() > new Date().getTime() && this.has(key);
  }

  public getItem(key: string) {
    return fromJSON(this.store.getItem(getItemKey(key)));
  }

  public clear() {
    this.store.clear();
  }
  
  public clearBy(regexp: RegExp) {
    for(let i =0; i < this.store.length; i++){
      const key = this.store.key(i);
      if(regexp.test(key)){
        this.store.removeItem(key);
      }
    }
  }

  public async removeItem(key: string) {
    const removed = this.getItem(key);
    this.store.removeItem(getItemKey(key));
    this.store.removeItem(getStampKey(key));
    return removed;
  }
}

class NullStorage implements CacheStorage {
  public length: number = 0;
  public has = (key: string): boolean => false;
  public clear = (): void => undefined;
  public clearBy = (regexp: RegExp): void => undefined;
  public getItem = (key: string): any => undefined;
  public removeItem = (key: string): any => undefined;
  public setItem = (key: string, data: any, stamp?: string): void => undefined;
  public hasValid = (key: string): boolean => false;
}

const clientCache = isBrowser ? new ClientCache() : new NullStorage();

export { clientCache };
